import React from "react";

import { faCircleCheck, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";

import { ButtonIcon } from "@performyard/py-components";
import type { ICellRendererParams } from "@performyard/py-components";
import type { BudgetDecisionData } from "@performyard/service-contracts";
import { toFixed } from "@performyard/utils-isomorphic";

import type { DecisionRow } from "../../types";
import { DecisionTableContext } from "../MyDecisions/types";

import { useCycleOwnerEditBudgetDecision } from "../../../../api/hooks";

import { coerceBudgetIncreaseNumber } from "./utils";

import { useIncreaseValue, useIsEditingRow } from "../MyDecisions/decision-store";

export const BudgetReviewRowEditCellRenderer = (params: ICellRendererParams<DecisionRow>): React.ReactNode => {
	const row = params.data;

	const context = params.context as DecisionTableContext;
	const storeValue = useIncreaseValue(context?.store, row?._id ?? "");
	const storeIsEditing = useIsEditingRow(context?.store, row?._id ?? "");
	const isEditingThisRow = context?.store
		? Boolean(row && !row.superRow && storeIsEditing)
		: Boolean(row && !row.superRow && context.editingRowIds?.has(row._id));
	const newEditId = row?._id ?? "";
	const cycleId = row?.compensation_cycle_id || "";
	const increaseVal = storeValue ?? context.increaseValues[newEditId];
	const noChange = toFixed(row?.proposed_increase_amount || 0, 2) === toFixed(increaseVal?.amount || 0, 2);
	const canEdit = Boolean(cycleId && newEditId && row && !noChange);
	const budgetDecisionData: BudgetDecisionData =
		row && canEdit
			? {
					employeeBeingUpdated: row.employee_id,
					increasePercent: coerceBudgetIncreaseNumber(increaseVal?.percent),
					increaseAmount: coerceBudgetIncreaseNumber(increaseVal?.amount),
					currentSalary: row?.current_salary ?? 0,
				}
			: { employeeBeingUpdated: "", increasePercent: 0, increaseAmount: 0, currentSalary: 0 };
	const { mutate: editBudgetDecisionRow } = useCycleOwnerEditBudgetDecision(cycleId, budgetDecisionData);

	if (!row || row.superRow) {
		return null;
	}
	const handleEdit = () => {
		if (canEdit) {
			editBudgetDecisionRow();
		} else {
			context.onBudgetReviewCancelEditRow?.(row);
		}
	};
	return (
		<div className="flex h-full items-center justify-end pr-1">
			{!isEditingThisRow && (
				<ButtonIcon
					variant="standard"
					color="neutral"
					shade={3}
					size={20}
					icon={faPencil}
					onClick={() => {
						context.onBudgetReviewEditRow?.(row);
					}}
					aria-label={`Edit ${row.employee_name}`}
				/>
			)}
			{isEditingThisRow && (
				<div className="inline-flex shrink-0 flex-row items-center gap-[2px]">
					<ButtonIcon
						variant="standard"
						color="green"
						shade={5}
						size={20}
						icon={faCircleCheck}
						onClick={handleEdit}
						aria-label={`Edit ${row.employee_name}`}
					/>
					<ButtonIcon
						variant="standard"
						color="red"
						shade={5}
						size={20}
						icon={faCircleXmark}
						onClick={() => {
							context.onBudgetReviewCancelEditRow?.(row);
						}}
						aria-label={`Edit ${row.employee_name}`}
					/>
				</div>
			)}
		</div>
	);
};
